import React from 'react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import CircularProgress from '../../../components/shared/CircularProgress';
import {
  Activity,
  Cpu,
  HardDrive,
  Database,
  Zap,
  Server,
  TrendingUp,
  Users,
  Car,
  Banknote,
  MessageSquare,
  Mail,
  ShieldAlert
} from 'lucide-react';

const MonitoringOverview = ({ 
  metrics, 
  realtimeStats, 
  activeAlerts, 
  onViewAlerts 
}) => {
  return (
    <>
      {/* Active Alerts Banner */}
      {activeAlerts.length > 0 && (
        <Card className="p-4 bg-red-50 border-red-300">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShieldAlert className="h-6 w-6 text-red-600" />
              <div>
                <p className="font-semibold text-red-800">
                  {activeAlerts.length} Active System Alert{activeAlerts.length > 1 ? 's' : ''}
                </p>
                <p className="text-sm text-red-600">
                  {activeAlerts[0]?.message}
                </p>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-100"
              onClick={onViewAlerts}
            >
              View Alerts
            </Button>
          </div>
        </Card>
      )}

      {/* Real-time Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Active Rides</p>
              <p className="text-3xl font-bold">{realtimeStats?.realtime?.activeRides || 0}</p>
            </div>
            <Car className="h-10 w-10 text-blue-200" />
          </div>
        </Card>
        
        <Card className="p-4 bg-gradient-to-br from-green-500 to-green-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Online Drivers</p>
              <p className="text-3xl font-bold">{realtimeStats?.realtime?.onlineDrivers || 0}</p>
            </div>
            <Users className="h-10 w-10 text-green-200" />
          </div>
        </Card>
        
        <Card className="p-4 bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Today's Rides</p>
              <p className="text-3xl font-bold">{realtimeStats?.today?.rides || 0}</p>
            </div>
            <TrendingUp className="h-10 w-10 text-purple-200" />
          </div>
        </Card>
        
        <Card className="p-4 bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">Today's Revenue</p>
              <p className="text-3xl font-bold">K{(realtimeStats?.today?.revenue || 0).toLocaleString()}</p>
            </div>
            <Banknote className="h-10 w-10 text-orange-200" />
          </div>
        </Card>
      </div>

      {/* System Resources */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* CPU */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Cpu className="h-5 w-5 text-blue-600" />
            </div>
            <h3 className="font-semibold">CPU Usage</h3>
          </div>
          <div className="flex justify-center">
            <CircularProgress 
              percent={metrics?.system?.cpu?.percent || 0} 
              color={metrics?.system?.cpu?.percent > 75 ? '#ef4444' : metrics?.system?.cpu?.percent > 50 ? '#eab308' : '#22c55e'}
            />
          </div>
          <div className="mt-4 text-center text-sm text-gray-600">
            {metrics?.system?.cpu?.cores || 0} CPU cores available
          </div>
        </Card>

        {/* Memory */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Server className="h-5 w-5 text-purple-600" />
            </div>
            <h3 className="font-semibold">Memory Usage</h3>
          </div>
          <div className="flex justify-center">
            <CircularProgress 
              percent={metrics?.system?.memory?.percent || 0}
              color={metrics?.system?.memory?.percent > 85 ? '#ef4444' : metrics?.system?.memory?.percent > 70 ? '#eab308' : '#22c55e'}
            />
          </div>
          <div className="mt-4 text-center text-sm text-gray-600">
            {metrics?.system?.memory?.used_gb || 0}GB / {metrics?.system?.memory?.total_gb || 0}GB
          </div>
        </Card>

        {/* Disk */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-orange-100 rounded-lg">
              <HardDrive className="h-5 w-5 text-orange-600" />
            </div>
            <h3 className="font-semibold">Disk Usage</h3>
          </div>
          <div className="flex justify-center">
            <CircularProgress 
              percent={metrics?.system?.disk?.percent || 0}
              color={metrics?.system?.disk?.percent > 90 ? '#ef4444' : metrics?.system?.disk?.percent > 75 ? '#eab308' : '#22c55e'}
            />
          </div>
          <div className="mt-4 text-center text-sm text-gray-600">
            {metrics?.system?.disk?.used_gb || 0}GB / {metrics?.system?.disk?.total_gb || 0}GB
          </div>
        </Card>
      </div>

      {/* Database & Cache Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Database */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-100 rounded-lg">
              <Database className="h-5 w-5 text-green-600" />
            </div>
            <h3 className="font-semibold">MongoDB</h3>
            <Badge className="bg-green-100 text-green-800">Connected</Badge>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-900">
                {metrics?.database?.connections?.current || 0}
              </p>
              <p className="text-xs text-gray-600">Active</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-900">
                {metrics?.database?.connections?.available || 0}
              </p>
              <p className="text-xs text-gray-600">Available</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-900">200</p>
              <p className="text-xs text-gray-600">Max Pool</p>
            </div>
          </div>
        </Card>

        {/* Cache */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Zap className="h-5 w-5 text-yellow-600" />
            </div>
            <h3 className="font-semibold">In-Memory Cache</h3>
            <Badge className="bg-yellow-100 text-yellow-800">Active</Badge>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-3xl font-bold text-green-600">
                {metrics?.cache?.hit_rate || '0%'}
              </p>
              <p className="text-sm text-green-700">Hit Rate</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-3xl font-bold text-blue-600">
                {metrics?.cache?.cached_items || 0}
              </p>
              <p className="text-sm text-blue-700">Cached Items</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Services Status */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-pink-100 rounded-lg">
            <Activity className="h-5 w-5 text-pink-600" />
          </div>
          <h3 className="font-semibold">External Services</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className={`p-4 rounded-lg border ${metrics?.services?.sms?.configured ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MessageSquare className={`h-6 w-6 ${metrics?.services?.sms?.configured ? 'text-green-600' : 'text-yellow-600'}`} />
                <div>
                  <p className="font-medium">SMS Service</p>
                  <p className="text-sm text-gray-600">Provider: {metrics?.services?.sms?.provider || 'Unknown'}</p>
                </div>
              </div>
              <Badge className={metrics?.services?.sms?.configured ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                {metrics?.services?.sms?.configured ? 'Active' : 'Mock Mode'}
              </Badge>
            </div>
          </div>

          <div className={`p-4 rounded-lg border ${metrics?.services?.email?.configured ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className={`h-6 w-6 ${metrics?.services?.email?.configured ? 'text-green-600' : 'text-yellow-600'}`} />
                <div>
                  <p className="font-medium">Email Service</p>
                  <p className="text-sm text-gray-600">Provider: {metrics?.services?.email?.provider || 'Unknown'}</p>
                </div>
              </div>
              <Badge className={metrics?.services?.email?.configured ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                {metrics?.services?.email?.configured ? 'Active' : 'Mock Mode'}
              </Badge>
            </div>
          </div>
        </div>
      </Card>
    </>
  );
};

export default MonitoringOverview;
